import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';

const BusinessAuthContext = createContext(null);

export const BusinessAuthProvider = ({ children }) => {
  const [businessCustomer, setBusinessCustomer] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(true);

  // Check for existing business token on mount
  useEffect(() => {
    const checkBusinessAuth = async () => {
      const token = localStorage.getItem('business_token');
      
      if (token) {
        try {
          const response = await axios.get(`${API_BASE}/business/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBusinessCustomer(response.data);
        } catch (error) {
          console.error('Business auth check failed:', error);
          localStorage.removeItem('business_token');
          setBusinessCustomer(null);
        }
      }
      
      setBusinessLoading(false);
    };

    checkBusinessAuth();
  }, []);

  const businessLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE}/business/login`, {
        email,
        password
      });
      
      const { access_token, business_name, email: businessEmail } = response.data;
      localStorage.setItem('business_token', access_token);
      setBusinessCustomer({
        business_name,
        email: businessEmail
      });
      
      return { success: true };
    } catch (error) {
      console.error('Business login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const businessLogout = () => {
    localStorage.removeItem('business_token');
    setBusinessCustomer(null);
  };

  const acceptInvitation = async (token, password) => {
    try {
      const response = await axios.post(`${API_BASE}/business/accept-invitation`, {
        token,
        password
      });
      
      return { 
        success: true, 
        message: 'Password set successfully. You can now log in.' 
      };
    } catch (error) {
      console.error('Accept invitation failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to accept invitation' 
      };
    }
  };

  const updateBusinessProfile = async (updates) => {
    const token = localStorage.getItem('business_token');
    
    try {
      const response = await axios.put(
        `${API_BASE}/business/profile`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBusinessCustomer(response.data);
      return { success: true };
    } catch (error) {
      console.error('Profile update failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Update failed' 
      };
    }
  };

  const value = {
    businessCustomer,
    businessLoading,
    businessLogin,
    businessLogout,
    acceptInvitation,
    updateBusinessProfile,
    isBusinessAuthenticated: !!businessCustomer
  };

  return (
    <BusinessAuthContext.Provider value={value}>
      {children}
    </BusinessAuthContext.Provider>
  );
};

export const useBusinessAuth = () => {
  const context = useContext(BusinessAuthContext);
  if (!context) {
    throw new Error('useBusinessAuth must be used within BusinessAuthProvider');
  }
  return context;
};
